import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Target, Check, X, Mic2 } from "lucide-react";
import { Link } from "react-router-dom";
import { useDashboardData } from "../../hooks/useDashboardData";
import { SectionHeader } from "../components/Shared";

// ─── Skeleton ─────────────────────────────────────────────────────────────────

const Skeleton = ({ className = "" }) => (
  <div className={`animate-pulse rounded-xl bg-panel/10 ${className}`} />
);

// ─── Build goals from real data ───────────────────────────────────────────────

function buildGoalsFromMemory(user, memory, sessionStats) {
  const goals = [];

  // Primary goal from user profile
  if (user?.learning_goal) {
    const totalSessions = sessionStats.logs.length;
    const progress = Math.min(95, totalSessions * 5); // 5% per session, max 95%
    const masteredCount = memory.mastered_vocabulary?.length || 0;
    const failedCount = memory.failed_vocabulary?.length || 0;

    goals.push({
      id: "primary",
      title: user.learning_goal,
      subtitle: `Learning ${user.target_language}`,
      progress,
      eta: progress < 20 ? "Just getting started" : progress < 50 ? "Making progress" : progress < 80 ? "Getting there" : "Almost there!",
      milestones: [
        { label: "First session completed", done: totalSessions >= 1 },
        { label: "5 vocabulary words mastered", done: masteredCount >= 5 },
        { label: "10 sessions completed", done: totalSessions >= 10 },
        { label: "Reached A2 level", done: ["A2", "B1", "B2", "C1", "C2"].includes(memory.cefr_level) },
        { label: "Reached B1 level", done: ["B1", "B2", "C1", "C2"].includes(memory.cefr_level) },
      ],
    });
  }

  // Next focus as a sub-goal
  if (memory.next_focus && memory.next_focus !== "Your first conversation with AURA") {
    goals.push({
      id: "next_focus",
      title: memory.next_focus,
      subtitle: "This session's focus",
      progress: 10,
      eta: "Next session",
      milestones: [
        { label: "Identified as focus area", done: true },
        { label: "Practice in live session", done: false },
        { label: "AURA confirms progress", done: false },
      ],
    });
  }

  // Vocabulary mastery goal if we have failed vocab
  if (memory.failed_vocabulary?.length > 0) {
    const cleared = memory.mastered_vocabulary?.length || 0;
    const total = (memory.failed_vocabulary?.length || 0) + cleared;
    const progress = total > 0 ? Math.round((cleared / total) * 100) : 0;
    goals.push({
      id: "vocab",
      title: `Clear vocabulary gaps`,
      subtitle: `${memory.failed_vocabulary.length} word${memory.failed_vocabulary.length !== 1 ? "s" : ""} to master`,
      progress,
      eta: `${memory.failed_vocabulary.length} remaining`,
      milestones: memory.failed_vocabulary.slice(0, 4).map((v) => ({
        label: `Master "${v.term}"`,
        done: false,
      })),
    });
  }

  return goals;
}

// ─── Mastered vocab as "completed goals" ─────────────────────────────────────

function buildCompletedGoals(memory) {
  return (memory.mastered_vocabulary || []).slice(-4).map((v) => ({
    id: v.term,
    title: `Mastered "${v.term}"`,
    completed_at: v.mastered_on ? new Date(v.mastered_on).toLocaleDateString() : "Recent",
  }));
}

// ─── Goals page ───────────────────────────────────────────────────────────────

const Goals = () => {
  const { user, memory, sessionStats, isLoading, isNewUser } = useDashboardData();
  const [open, setOpen] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newSub, setNewSub] = useState("");
  // Local goals added this session (not persisted — future work)
  const [localGoals, setLocalGoals] = useState([]);

  const addGoal = (e) => {
    e?.preventDefault?.();
    const t = newTitle.trim();
    if (!t) return;
    setLocalGoals((prev) => [{
      id: `local-${Date.now()}`,
      title: t,
      subtitle: newSub.trim() || "Added by you",
      progress: 0,
      eta: "Just added",
      milestones: [{ label: "Get started", done: false }],
    }, ...prev]);
    setNewTitle(""); setNewSub(""); setOpen(false);
  };

  const activeGoals = isLoading ? [] : [
    ...buildGoalsFromMemory(user, memory, sessionStats),
    ...localGoals,
  ];

  const completedGoals = isLoading ? [] : buildCompletedGoals(memory);

  return (
    <div className="max-w-7xl mx-auto" data-testid="page-goals">
      <div className="flex items-end justify-between gap-3 mb-6">
        <div>
          <div className="section-label">Goals</div>
          <h1 className="mt-2 font-display text-[28px] sm:text-[36px] font-light tracking-tight text-ink leading-tight">
            Where you're <em>going</em>.
          </h1>
        </div>
        <button onClick={() => setOpen(true)} className="btn-primary py-2 px-4 text-[10px]" data-testid="goals-add">
          <Plus size={12} /> add goal
        </button>
      </div>

      {/* New user state */}
      {!isLoading && isNewUser && activeGoals.length === 0 && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          className="rounded-3xl glass edge-glow p-8 text-center mb-6"
        >
          <div className="w-16 h-16 rounded-2xl bg-aura-cyan/10 border border-aura-cyan/20 grid place-items-center text-aura-cyan mx-auto">
            <Target size={28} />
          </div>
          <h2 className="mt-4 font-display text-[22px] font-light text-ink">Complete your first session to unlock goals.</h2>
          <p className="mt-2 text-whisper text-[14px] max-w-md mx-auto">
            AURA will automatically create personalised goals based on what she observes in your first conversation.
          </p>
          <Link to="/dashboard/session" className="btn-primary mt-6 inline-flex items-center gap-2 text-[12px]">
            <Mic2 size={14} /> Start first session
          </Link>
        </motion.div>
      )}

      {/* Active goals */}
      <div className="grid lg:grid-cols-2 gap-4" data-testid="goals-active">
        {isLoading ? (
          Array.from({ length: 2 }).map((_, i) => <Skeleton key={i} className="h-64 rounded-3xl" />)
        ) : (
          activeGoals.map((g) => (
            <motion.div key={g.id} initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4 }}
              className="rounded-3xl glass edge-glow p-6"
              data-testid={`goal-card-${g.id}`}
            >
              <div className="flex items-center gap-3">
                <span className="w-10 h-10 rounded-xl grid place-items-center bg-aura-cyan/10 border border-aura-cyan/20 text-aura-cyan">
                  <Target size={16} />
                </span>
                <div className="min-w-0">
                  <div className="font-display text-[20px] font-light text-ink truncate">{g.title}</div>
                  <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-whisper-muted">{g.subtitle}</div>
                </div>
                <span className="ml-auto pill text-[10px]">{g.eta}</span>
              </div>
              <div className="mt-4 h-2 rounded-full bg-panel/5 overflow-hidden">
                <motion.div initial={{ width: 0 }} whileInView={{ width: `${g.progress}%` }} viewport={{ once: true }} transition={{ duration: 1 }} className="h-full bg-gradient-to-r from-aura-cyan via-aura-blue to-aura-violet" />
              </div>
              <div className="mt-2 flex justify-between font-mono text-[10px] tracking-[0.22em] uppercase text-whisper-muted">
                <span>{g.progress}% there</span>
                <span>aura's roadmap →</span>
              </div>
              <div className="mt-5 space-y-2">
                {g.milestones.map((m, i) => (
                  <div key={i} className="flex items-center gap-3 text-[13.5px] text-ink/90">
                    <span className={`w-5 h-5 rounded-full grid place-items-center ${m.done ? "bg-aura-cyan/15 border border-aura-cyan/30 text-aura-cyan" : "border border-panel/15 text-whisper-muted"}`}>
                      {m.done ? <Check size={12} /> : <span className="w-1.5 h-1.5 bg-whisper-muted rounded-full" />}
                    </span>
                    <span className={m.done ? "" : "text-whisper"}>{m.label}</span>
                  </div>
                ))}
              </div>
              <div className="mt-5 flex gap-2">
                <button className="btn-ghost py-2 px-3 text-[10px]">edit</button>
                <Link to="/dashboard/session" className="btn-ghost py-2 px-3 text-[10px] border-aura-cyan/30 text-aura-cyan">practice now</Link>
              </div>
            </motion.div>
          ))
        )}
      </div>

      {/* Completed / mastered vocab as archived goals */}
      {completedGoals.length > 0 && (
        <div className="mt-10">
          <SectionHeader eyebrow="Mastered · archive" title="Words AURA helped you own." />
          <div className="grid sm:grid-cols-2 gap-3" data-testid="goals-archive">
            {completedGoals.map((g) => (
              <div key={g.id} className="hairline rounded-2xl p-4 flex items-center justify-between gap-3">
                <div>
                  <div className="font-display text-[15px] text-ink">{g.title}</div>
                  <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-whisper-muted mt-1">mastered · {g.completed_at}</div>
                </div>
                <span className="pill text-[10px]">✓ done</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Add goal modal */}
      <AnimatePresence>
        {open && (
          <motion.div className="fixed inset-0 z-50 grid place-items-center px-4" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} data-testid="goals-modal">
            <div className="absolute inset-0 bg-base/70 backdrop-blur-md" onClick={() => setOpen(false)} />
            <motion.form onSubmit={addGoal} initial={{ y: 16, scale: 0.98 }} animate={{ y: 0, scale: 1 }} className="relative w-full max-w-[480px] rounded-3xl glass edge-glow p-6">
              <div className="flex items-center justify-between">
                <div className="section-label">New goal</div>
                <button type="button" onClick={() => setOpen(false)} aria-label="Close"><X size={14} className="text-whisper-muted hover:text-ink" /></button>
              </div>
              <input value={newTitle} onChange={(e) => setNewTitle(e.target.value)} placeholder="e.g. C1 by next summer" className="mt-4 w-full bg-panel/[0.02] border border-panel/10 rounded-2xl p-3 text-[15px] text-ink placeholder:text-whisper-muted focus:outline-none focus:border-aura-cyan/60" data-testid="goals-input-title" />
              <input value={newSub} onChange={(e) => setNewSub(e.target.value)} placeholder="why? (e.g. PhD program in Madrid)" className="mt-3 w-full bg-panel/[0.02] border border-panel/10 rounded-2xl p-3 text-[13.5px] text-ink placeholder:text-whisper-muted focus:outline-none focus:border-aura-cyan/60" data-testid="goals-input-sub" />
              <div className="mt-5 flex justify-end gap-2">
                <button type="button" onClick={() => setOpen(false)} className="btn-ghost text-[10px] py-2 px-3">cancel</button>
                <button type="submit" className="btn-primary text-[10px] py-2 px-3" data-testid="goals-save">save goal</button>
              </div>
            </motion.form>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Goals;
